import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];
const MEAL_COLORS = { breakfast: 'amber', lunch: 'emerald', dinner: 'indigo' };
const MEAL_ICONS = { breakfast: 'Coffee', lunch: 'UtensilsCrossed', dinner: 'Moon' };
const CATEGORY_COLORS = { student: 'sky', resident: 'violet', staff: 'rose', visitor: 'orange' };

function QRCodeDisplay({ value, size = 80 }) {
  // Simple QR-like visual using the ticket number as seed
  const cells = 9;
  const seed = value?.split('')?.reduce((a, c) => a + c?.charCodeAt(0), 0) || 0;
  const grid = Array.from({ length: cells * cells }, (_, i) => {
    const x = i % cells;
    const y = Math.floor(i / cells);
    // Corner markers
    if ((x < 3 && y < 3) || (x >= cells - 3 && y < 3) || (x < 3 && y >= cells - 3)) return true;
    return ((seed * (i + 1) * 7) % 17) > 8;
  });
  const cellSize = size / cells;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rounded">
      <rect width={size} height={size} fill="white" />
      {grid?.map((filled, i) => filled ? (
        <rect
          key={i}
          x={(i % cells) * cellSize}
          y={Math.floor(i / cells) * cellSize}
          width={cellSize}
          height={cellSize}
          fill="#1a1a1a"
        />
      ) : null)}
    </svg>
  );
}

function PrintableTicket({ ticket, cardholder }) {
  const mealColor = MEAL_COLORS?.[ticket?.meal_type] || 'slate';
  const mealIcon = MEAL_ICONS?.[ticket?.meal_type] || 'Utensils';
  return (
    <div className={`ticket-card border-2 rounded-xl p-4 bg-white space-y-2 relative overflow-hidden border-${mealColor}-300`} style={{ width: 220, minHeight: 280 }}>
      {/* Header stripe */}
      <div className={`absolute top-0 left-0 right-0 h-1.5 bg-${mealColor}-400`} />
      <div className="flex items-center justify-between pt-1">
        <span className={`text-xs font-bold uppercase tracking-widest text-${mealColor}-600`}>
          {ticket?.meal_type}
        </span>
        <Icon name={mealIcon} size={14} className={`text-${mealColor}-500`} />
      </div>
      <div className="border-t border-dashed border-slate-200 pt-2">
        <div className="text-xs text-slate-400 font-medium uppercase tracking-wide">Name</div>
        <div className="text-sm font-bold text-slate-800 leading-tight">{cardholder?.full_name || ticket?.restaurant_cardholders?.full_name}</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-xs text-slate-400 font-medium uppercase tracking-wide">Category</div>
          <div className="text-xs font-semibold text-slate-700 capitalize">{cardholder?.category || ticket?.restaurant_cardholders?.category}</div>
        </div>
        <div>
          <div className="text-xs text-slate-400 font-medium uppercase tracking-wide">Date</div>
          <div className="text-xs font-semibold text-slate-700">{ticket?.valid_date}</div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-400 font-medium uppercase tracking-wide">Status</div>
          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${ticket?.status === 'used' ? 'bg-slate-100 text-slate-500' : ticket?.status === 'expired' ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>
            {ticket?.status?.toUpperCase()}
          </span>
        </div>
        <QRCodeDisplay value={ticket?.ticket_number} size={56} />
      </div>
      <div className="border-t border-dashed border-slate-200 pt-1">
        <div className="text-xs text-slate-400 font-mono truncate">{ticket?.ticket_number}</div>
      </div>
    </div>
  );
}

export default function TicketGeneration({ isAdmin }) {
  const [cardholders, setCardholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedMeals, setSelectedMeals] = useState(['breakfast', 'lunch', 'dinner']);
  const [dateMode, setDateMode] = useState('single'); // single | range
  const [validDate, setValidDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [dateFrom, setDateFrom] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [dateTo, setDateTo] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [filterDate, setFilterDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [filterMeal, setFilterMeal] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const printRef = useRef(null);

  useEffect(() => { fetchCardholders(); fetchTickets(); }, []);
  useEffect(() => { fetchTickets(); }, [filterDate, filterMeal, filterStatus]);

  const fetchCardholders = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('restaurant_cardholders')
        ?.select('id, full_name, category, building, department, accommodation_end, is_active')
        ?.eq('is_active', true)
        ?.order('full_name');
      if (!error) setCardholders(data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      let query = supabase
        ?.from('restaurant_meal_tickets')
        ?.select('*, restaurant_cardholders(full_name, category, building)')
        ?.eq('valid_date', filterDate)
        ?.order('created_at', { ascending: false });
      if (filterMeal !== 'all') query = query?.eq('meal_type', filterMeal);
      if (filterStatus !== 'all') query = query?.eq('status', filterStatus);
      const { data, error } = await query;
      if (!error) setTickets(data || []);
    } catch (e) { console.error(e); }
    finally { setTicketsLoading(false); }
  };

  const filteredCardholders = cardholders?.filter(c => {
    const matchCat = filterCategory === 'all' || c?.category === filterCategory;
    if (!searchQuery) return matchCat;
    const q = searchQuery?.toLowerCase();
    return matchCat && (c?.full_name?.toLowerCase()?.includes(q) || c?.category?.toLowerCase()?.includes(q) || c?.building?.toLowerCase()?.includes(q));
  });

  const toggleSelect = (id) => setSelectedIds(prev => prev?.includes(id) ? prev?.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    const ids = filteredCardholders?.map(c => c?.id);
    setSelectedIds(prev => prev?.length === ids?.length ? [] : ids);
  };
  const toggleMeal = (meal) => setSelectedMeals(prev => prev?.includes(meal) ? prev?.filter(m => m !== meal) : [...prev, meal]);

  const getDatesInRange = (from, to) => {
    const dates = [];
    let cur = new Date(from);
    const end = new Date(to);
    while (cur <= end) {
      dates?.push(cur?.toISOString()?.split('T')?.[0]);
      cur?.setDate(cur?.getDate() + 1);
    }
    return dates;
  };

  const generateTickets = async () => {
    if (!selectedIds?.length || !selectedMeals?.length) return;
    setGenerating(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const dates = dateMode === 'range' ? getDatesInRange(dateFrom, dateTo) : [validDate];
      const inserts = [];
      for (const date of dates) {
        for (const chId of selectedIds) {
          for (const meal of selectedMeals) {
            inserts?.push({
              cardholder_id: chId,
              meal_type: meal,
              valid_date: date,
              status: 'active',
              generated_by: user?.id,
              ticket_number: '',
            });
          }
        }
      }
      const { error } = await supabase?.from('restaurant_meal_tickets')?.insert(inserts);
      if (error) {
        setErrorMsg(error?.message?.includes('unique') ? 'Some tickets already exist for the selected date(s). Duplicates were skipped.' : error?.message);
      } else {
        setSuccessMsg(`${inserts?.length} ticket(s) generated successfully.`);
        setSelectedIds([]);
        fetchTickets();
      }
    } catch (e) {
      setErrorMsg(e?.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const cancelTicket = async (id) => {
    const { error } = await supabase?.from('restaurant_meal_tickets')?.update({ status: 'cancelled' })?.eq('id', id);
    if (!error) fetchTickets();
  };

  const handlePrint = () => {
    const printContent = printRef?.current?.innerHTML;
    if (!printContent) return;
    const w = window.open('', '_blank');
    w?.document?.write(`<html><head><title>Meal Tickets</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; padding: 20px; background: #f8f8f8; }
        .tickets-grid { display: flex; flex-wrap: wrap; gap: 12px; }
        .ticket-card { border: 2px solid #d1d5db; border-radius: 12px; padding: 14px; background: white; width: 200px; min-height: 260px; position: relative; overflow: hidden; display: inline-block; vertical-align: top; page-break-inside: avoid; }
        @media print { body { padding: 10px; background: white; } .ticket-card { page-break-inside: avoid; } }
      </style></head><body><div class="tickets-grid">${printContent}</div></body></html>`);
    w?.document?.close();
    w?.print();
  };

  const statusBadge = (s) => {
    const map = { active: 'bg-emerald-100 text-emerald-700', used: 'bg-slate-100 text-slate-600', expired: 'bg-red-100 text-red-600', cancelled: 'bg-orange-100 text-orange-600' };
    return map?.[s] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="space-y-6">
      {/* Generation Panel */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-5">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Icon name="Zap" size={16} className="text-amber-500" />
          Generate Meal Tickets
        </h3>

        {successMsg && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-2.5 text-sm">
            <Icon name="CheckCircle2" size={15} />
            {successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 text-sm">
            <Icon name="AlertCircle" size={15} />
            {errorMsg}
          </div>
        )}

        {/* Date Mode */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2">Date Mode</label>
            <div className="flex gap-2">
              <button onClick={() => setDateMode('single')}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all ${dateMode === 'single' ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-muted border-border text-muted-foreground'}`}>
                Single Date
              </button>
              <button onClick={() => setDateMode('range')}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all ${dateMode === 'range' ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-muted border-border text-muted-foreground'}`}>
                Date Range
              </button>
            </div>
          </div>
          <div>
            {dateMode === 'single' ? (
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Valid Date</label>
                <input type="date" value={validDate} onChange={e => setValidDate(e?.target?.value)}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-300" />
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">From</label>
                  <input type="date" value={dateFrom} onChange={e => setDateFrom(e?.target?.value)}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-300" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">To</label>
                  <input type="date" value={dateTo} onChange={e => setDateTo(e?.target?.value)}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-300" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Meal Types */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-2">Meal Types</label>
          <div className="flex gap-2">
            {MEAL_TYPES?.map(m => (
              <button key={m} onClick={() => toggleMeal(m)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold border transition-all ${selectedMeals?.includes(m) ? `bg-${MEAL_COLORS?.[m]}-100 border-${MEAL_COLORS?.[m]}-300 text-${MEAL_COLORS?.[m]}-700` : 'bg-muted border-border text-muted-foreground'}`}>
                <Icon name={MEAL_ICONS?.[m]} size={13} />
                {m?.charAt(0)?.toUpperCase() + m?.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Cardholder Selection */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <label className="text-xs font-medium text-muted-foreground">
              Select Recipients ({selectedIds?.length} selected)
            </label>
            <div className="flex gap-2 flex-wrap">
              <select value={filterCategory} onChange={e => setFilterCategory(e?.target?.value)}
                className="border border-border rounded-lg px-2 py-1.5 text-xs bg-background text-foreground focus:outline-none">
                <option value="all">All Categories</option>
                <option value="student">Students</option>
                <option value="resident">Residents</option>
                <option value="staff">Staff</option>
                <option value="visitor">Visitors</option>
              </select>
              <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e?.target?.value)}
                className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none w-36" />
              <button onClick={toggleSelectAll} className="text-xs text-amber-600 font-semibold hover:underline">
                {selectedIds?.length === filteredCardholders?.length && filteredCardholders?.length > 0 ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8"><Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="max-h-56 overflow-y-auto border border-border rounded-lg divide-y divide-border">
              {filteredCardholders?.map(c => (
                <label key={c?.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer">
                  <input type="checkbox" checked={selectedIds?.includes(c?.id)} onChange={() => toggleSelect(c?.id)}
                    className="rounded border-border text-amber-500 focus:ring-amber-300" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{c?.full_name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{c?.category}{c?.building ? ` · ${c?.building}` : ''}</div>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium bg-${CATEGORY_COLORS?.[c?.category]}-100 text-${CATEGORY_COLORS?.[c?.category]}-700 capitalize`}>
                    {c?.category}
                  </span>
                  {c?.accommodation_end && new Date(c.accommodation_end) < new Date() && (
                    <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Expired</span>
                  )}
                </label>
              ))}
              {filteredCardholders?.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">No cardholders found</div>
              )}
            </div>
          )}
        </div>

        <button onClick={generateTickets} disabled={generating || !selectedIds?.length || !selectedMeals?.length}
          className="w-full py-2.5 rounded-lg bg-amber-500 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2 transition-all hover:bg-amber-600">
          {generating ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="Zap" size={15} />}
          Generate {selectedIds?.length > 0 ? `${selectedIds?.length * selectedMeals?.length}${dateMode === 'range' ? `×${getDatesInRange(dateFrom, dateTo)?.length}d` : ''} Ticket(s)` : 'Tickets'}
        </button>
      </div>

      {/* Tickets List */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="TicketCheck" size={16} className="text-amber-500" />
            Meal Tickets
          </h3>
          <div className="flex flex-wrap gap-2">
            <input type="date" value={filterDate} onChange={e => setFilterDate(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none" />
            <select value={filterMeal} onChange={e => setFilterMeal(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none">
              <option value="all">All Meals</option>
              {MEAL_TYPES?.map(m => <option key={m} value={m}>{m?.charAt(0)?.toUpperCase() + m?.slice(1)}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)}
              className="border border-border rounded-lg px-3 py-1.5 text-xs bg-background text-foreground focus:outline-none">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="used">Used</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground hover:bg-muted transition-all">
              <Icon name="Printer" size={13} />Print All
            </button>
          </div>
        </div>

        {ticketsLoading ? (
          <div className="flex items-center justify-center py-10"><Icon name="Loader2" size={22} className="animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div ref={printRef} className="tickets-grid flex flex-wrap gap-3">
              {tickets?.map(t => (
                <div key={t?.id} className="relative group">
                  <PrintableTicket ticket={t} cardholder={t?.restaurant_cardholders} />
                  {isAdmin && t?.status === 'active' && (
                    <button onClick={() => cancelTicket(t?.id)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded-full bg-red-100 text-red-500 flex items-center justify-center hover:bg-red-200"
                      title="Cancel ticket">
                      <Icon name="X" size={11} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {tickets?.length === 0 && (
              <div className="py-10 text-center">
                <Icon name="Ticket" size={32} className="text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No tickets found for this date</p>
              </div>
            )}
            {tickets?.length > 0 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-3">
                <span>{tickets?.length} ticket(s) shown</span>
                <span>{tickets?.filter(t => t?.status === 'used')?.length} used · {tickets?.filter(t => t?.status === 'active')?.length} active</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
